import { apiWithAuth } from "@/lib/axios";
import type { Skill } from "./types";
import { emitCopilotMutation } from "../mutations";

const PAYMENT_METHODS = ["cash", "card", "transfer", "courtesy"] as const;
const METHOD_LABELS: Record<string, string> = {
  cash: "Efectivo",
  card: "Tarjeta",
  transfer: "Transferencia",
  courtesy: "Cortesía",
};

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export const closeOrderSkill: Skill = {
  id: "closeOrder",
  description:
    "Cierra (cobra) una orden activa registrando el método de pago. Acción IRREVERSIBLE — el pedido queda como 'cerrado'. Necesita orderId, cashRegisterId y paymentMethod. Si el usuario no sabe el cashRegisterId, sugiérele que abra el tab 'caja' o use sesiones-caja para ver las cajas activas.",
  schema: {
    type: "object",
    properties: {
      restaurantId: { type: "string" },
      orderId: { type: "string", description: "ID de la orden a cerrar" },
      cashRegisterId: {
        type: "string",
        description: "ID de la caja registradora que registra el pago",
      },
      paymentMethod: {
        type: "string",
        enum: [...PAYMENT_METHODS],
        description: "Método de pago",
      },
      paymentDetails: {
        type: "string",
        description:
          "Detalle adicional (opcional). Ej. 'Nequi' para transferencia, 'Visa' para tarjeta.",
      },
      clientId: {
        type: "string",
        description: "ID de cliente a asociar al pedido (opcional)",
      },
    },
    required: ["restaurantId", "orderId", "cashRegisterId", "paymentMethod"],
  },
  async run(args, ctx) {
    if (!ctx.accessToken) {
      return { ok: false, error: "Sesión no disponible." };
    }
    const restaurantId = String(args.restaurantId ?? "");
    const orderId = String(args.orderId ?? "");
    const cashRegisterId = String(args.cashRegisterId ?? "");
    const paymentMethod = String(args.paymentMethod ?? "");
    if (!restaurantId || !orderId || !cashRegisterId || !paymentMethod) {
      return { ok: false, error: "Faltan datos requeridos." };
    }
    if (
      !PAYMENT_METHODS.includes(
        paymentMethod as (typeof PAYMENT_METHODS)[number],
      )
    ) {
      return {
        ok: false,
        error: `Método de pago inválido: ${paymentMethod}.`,
      };
    }
    const paymentDetails = args.paymentDetails
      ? String(args.paymentDetails)
      : undefined;
    const clientId = args.clientId ? String(args.clientId) : undefined;

    const ok = await ctx.confirmation.confirm({
      title: "Cerrar pedido",
      description:
        "Vas a cobrar y cerrar este pedido. La acción es IRREVERSIBLE.",
      details: [
        { label: "Pedido", value: orderId },
        { label: "Pago", value: METHOD_LABELS[paymentMethod] ?? paymentMethod },
        ...(paymentDetails
          ? [{ label: "Detalle", value: paymentDetails }]
          : []),
        { label: "Caja", value: cashRegisterId },
      ],
      confirmLabel: "Cerrar pedido",
      danger: true,
    });
    if (!ok) {
      return { ok: false, error: "Acción cancelada por el usuario." };
    }

    if (ctx.follow.enabled) {
      await ctx.follow.step("Cerrando pedido…", { durationMs: 600 });
      await ctx.follow.clickAt(`[data-copilot-tab="caja"]`, {
        durationMs: 700,
        label: "Cobrar Orden",
      });
      ctx.router.push(`/dashboard/restaurantes/${restaurantId}?tab=caja`);
      await sleep(900);
      await ctx.follow.step(
        `Registrando pago: ${METHOD_LABELS[paymentMethod] ?? paymentMethod}`,
        { durationMs: 600 },
      );
    }

    try {
      const body: Record<string, unknown> = {
        cashRegisterId,
        paymentMethod,
      };
      if (paymentDetails) body.paymentDetails = paymentDetails;
      if (clientId) body.clientId = clientId;

      const { data } = await apiWithAuth.post(
        `/restaurants/${restaurantId}/orders/${orderId}/close`,
        body,
        ctx.accessToken,
      );

      if (ctx.follow.enabled) {
        await ctx.follow.step("✓ Pedido cerrado", {
          durationMs: 900,
          spinner: false,
        });
        ctx.follow.clear();
      }

      emitCopilotMutation({
        scope: "orders",
        action: "closed",
        restaurantId,
        id: orderId,
      });

      return {
        ok: true,
        result: {
          id: data?.id ?? orderId,
          status: data?.status ?? "closed",
          total: data?.total,
          paymentMethod,
        },
        humanSummary: `Pedido cerrado con ${METHOD_LABELS[paymentMethod] ?? paymentMethod}.`,
      };
    } catch (err: any) {
      if (ctx.follow.enabled) {
        await ctx.follow.step("✗ Error al cerrar pedido", {
          durationMs: 700,
          spinner: false,
        });
        ctx.follow.clear();
      }
      return {
        ok: false,
        error:
          err?.response?.data?.message ||
          err?.message ||
          "Error cerrando pedido.",
      };
    }
  },
};
